import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Button, Modal as BootstrapModal, FormControl, HelpBlock, Label } from 'react-bootstrap';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { createCustomConcept } from '../../api/snomed';
import { WithAuth } from '../Authorization/WithAuth';
import { HandleApiError } from '../Notifications/APIErrorHandler';

// Términos propios del centro (specs/features/MedicalRecords/terminos-propios-items.md).
// RN-6: letras (con tildes/ñ), números, espacios y guiones. RN-6a: máximo 255 caracteres.
const CUSTOM_TERM_MAX_LENGTH = 255;
const CUSTOM_TERM_PATTERN = /^[\p{L}\p{N}\s-]+$/u;
const POSSIBLE_DUPLICATE_ERROR = 'medicalRecordsErrors.Concept_PossibleDuplicate';

const getPossibleDuplicates = error => {
    const data = error?.response?.data;
    if (error?.response?.status !== 409 || !data)
        return null;

    if ((data.Error ?? data.error) !== POSSIBLE_DUPLICATE_ERROR)
        return null;

    const similar = data.Data ?? data.data;
    return Array.isArray(similar) ? similar : null;
};

// El backend serializa el Concept en camelCase, pero si llega por el APIError del BFF puede venir en PascalCase.
const toOption = concept => ({
    id: concept.id ?? concept.Id,
    conceptId: concept.conceptId ?? concept.ConceptId ?? null,
    description: concept.description ?? concept.Description,
    interopSystem: concept.interopSystem ?? concept.InteropSystem,
    usageCount: concept.usageCount ?? concept.UsageCount ?? 0
});

export const CustomConceptCreator = props => {
    const { conceptType, onCreated, disabled } = props;
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [similar, setSimilar] = useState(null);

    const validationSchema = Yup.object().shape({
        term: Yup.string()
            .trim()
            .required(t('commons.customTerm.required'))
            .max(CUSTOM_TERM_MAX_LENGTH, t('commons.customTerm.tooLong', { max: CUSTOM_TERM_MAX_LENGTH }))
            .matches(CUSTOM_TERM_PATTERN, t('commons.customTerm.invalidCharacters'))
    });

    const close = () => {
        setIsOpen(false);
        setSimilar(null);
    };

    const select = concept => {
        onCreated(toOption(concept));
        close();
    };

    const create = async (term, confirmDespiteSimilar) => {
        try {
            const response = await createCustomConcept(conceptType, term.trim(), confirmDespiteSimilar);
            select(response.data);
            return true;
        }
        catch (error) {
            const possibleDuplicates = getPossibleDuplicates(error);
            if (possibleDuplicates)
                setSimilar({ term, items: possibleDuplicates.map(toOption) });
            else
                HandleApiError(error);
            return false;
        }
    };

    if (disabled)
        return null;

    if (!isOpen)
        return (
            <WithAuth requiredPermission={["MedicalRecords.Create"]}>
                <button type="button" className="btn btn-link btn-xs" style={{ paddingLeft: 0, marginTop: '6px' }} onClick={() => setIsOpen(true)}>
                    {t('commons.addNew')}
                </button>
            </WithAuth>
        );

    return (
        <Formik
            initialValues={{ term: '' }}
            validationSchema={validationSchema}
            onSubmit={async (values, actions) => {
                // Si se seleccionó el término, este formulario ya se desmontó.
                if (!await create(values.term, false))
                    actions.setSubmitting(false);
            }}
            render={({ values, errors, touched, handleChange, handleBlur, submitForm, isSubmitting }) => (
                <div style={{ marginTop: '5px' }}>
                    <FormControl
                        type="text"
                        name="term"
                        autoFocus
                        placeholder={t('commons.customTerm.placeholder')}
                        value={values.term}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onKeyDown={e => {
                            // Este control vive dentro del <form> del item: Enter no debe enviar el formulario padre.
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                submitForm();
                            }
                        }}
                    />
                    {touched.term && errors.term && <HelpBlock className="text-danger">{errors.term}</HelpBlock>}
                    <div style={{ marginTop: '5px' }}>
                        <button type="button" className="btn btn-default btn-xs" onClick={close} disabled={isSubmitting}>
                            {t('buttons.cancel')}
                        </button>{' '}
                        {/* Vacío o solo espacios: Guardar deshabilitado (CP-C03). El resto de RN-6 se valida inline al guardar. */}
                        <button type="button" className="btn btn-success btn-xs" onClick={submitForm} disabled={isSubmitting || !values.term.trim()}>
                            {t('buttons.save')}
                        </button>
                    </div>
                    {
                        similar &&
                        <ModalSimilarConcepts
                            term={similar.term}
                            items={similar.items}
                            onUseExisting={select}
                            onCreateAnyway={() => create(similar.term, true)}
                            onHide={() => setSimilar(null)}
                        />
                    }
                </div>
            )}
        />
    );
};

CustomConceptCreator.propTypes = {
    conceptType: PropTypes.string.isRequired,
    onCreated: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};

// RN-6c / CU-7: advertencia de términos parecidos. Variante de ModalDuplicatedItem: además de informar,
// deja elegir entre usar el existente o crear el término igual.
export const ModalSimilarConcepts = props => {
    const { term, items, onUseExisting, onCreateAnyway, onHide } = props;
    const { t } = useTranslation();

    return ReactDOM.createPortal(
        <BootstrapModal show={true} onHide={onHide} backdrop="static" bsStyle="warning">
            <BootstrapModal.Header closeButton>
                <BootstrapModal.Title>{t('commons.attention')}</BootstrapModal.Title>
            </BootstrapModal.Header>
            <BootstrapModal.Body>
                <p>{t('commons.customTerm.similarFound', { term })}</p>
                <ul className="list-unstyled">
                    {
                        items.map(item => (
                            <li key={item.id} style={{ marginBottom: '5px' }}>
                                <Button bsStyle="primary" bsSize="xsmall" onClick={() => onUseExisting(item)}>
                                    {t('commons.customTerm.useExisting', { term: item.description })}
                                </Button>{' '}
                                {!item.conceptId && <Label>{t('commons.customTerm.badge')}</Label>}
                            </li>
                        ))
                    }
                </ul>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <Button bsStyle="default" onClick={onHide}>{t('buttons.cancel')}</Button>
                <Button bsStyle="warning" onClick={onCreateAnyway}>{t('commons.customTerm.createAnyway')}</Button>
            </BootstrapModal.Footer>
        </BootstrapModal>, document.body
    );
};

ModalSimilarConcepts.propTypes = {
    term: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    onUseExisting: PropTypes.func.isRequired,
    onCreateAnyway: PropTypes.func.isRequired,
    onHide: PropTypes.func.isRequired
};
